import { GridFilterItem, GridSortItem } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  EMPTY_FILTERS,
  LabelValue,
  buildColumnDefs,
  buildConditionOptions,
  buildContractOptions,
  buildFilterModel,
  buildInitialColumnSelection,
  parseMultiSerial,
} from './partListFilters';
import { parsePartListUrl, writePartListUrl } from './partListUrlState';
import { NodeType } from '../../../@types/structure.type';
import { FilterState } from '../../../components/molecules/FilterBar';
import {
  ColumnDef,
  SortModel,
  applyColumnOrder,
  reorderByVisibleIndex,
} from '../../../components/organisms/ServiceTable';
import { ColumnSelectionItem } from '../../../components/templates/ColumnSelection';
import { StructureTypeCategory } from '../../../configs/enums';
import { useColumnRefetchScheduler } from '../../../hooks/useColumnRefetchScheduler';
import { useRefetchLoopGuard } from '../../../hooks/useRefetchLoopGuard';
import { useSessionColumns } from '../../../hooks/useSessionColumns';
import { useGetAllParts, useGetPartConditions } from '../../../queries/part-query';
import { useGetStructuresByTypeCategoryCodeAndTypeCode } from '../../../queries/structure-query';
import { useGetPartAllocatedTags } from '../../../queries/tags-query';
import { getVisibleFields } from '../../../utils/columnSelectionOperations';
import { sanitizeSavedReportFilterItems } from '../../../utils/filterModel';
import type { SortToken } from '../../../utils/listUrlState';
import {
  applySessionColumnState,
  dropColumnWidths,
  readSessionColumnState,
} from '../../../utils/sessionColumnState';

export interface PartListDataReturn {
  data: Array<Record<string, unknown>>;
  isLoading: boolean;
  isFetching: boolean;
  page: number;
  pageSize: number;
  total: number;
  fetchDurationMs: number | null;
  sortModel: SortModel;
  columns: ColumnDef[];
  columnSelectionData: ColumnSelectionItem[];
  filterModel: { items: GridFilterItem[] };
  conditionOptions: LabelValue[];
  tagOptions: LabelValue[];
  contractOptions: LabelValue[];
  setPage: (p: number) => void;
  setPageSize: (s: number) => void;
  setSortModel: (m: SortModel) => void;
  applyFilters: (filters: FilterState) => void;
  applyReportConfig: (
    filterModel: { items: GridFilterItem[] },
    colSelection?: ColumnSelectionItem[],
    columnOrder?: Array<{ field: string }>
  ) => void;
  updateColumnSelection: (data: ColumnSelectionItem[]) => void;
  reorderColumns: (oldIndex: number, newIndex: number) => void;
  /** Restore the default view's own column layout (session, else built defaults)
   *  so returning to the default tab doesn't inherit a report's columns. */
  resetColumnsToDefault: () => void;
  /** Discard the saved session layout and rebuild from the built-in defaults
   *  — the column picker's "Reset to default". */
  restoreDefaultColumns: () => void;
  /** Set a single column's pixel width (from a header resize-drag). `seed` is
   *  the one-time snapshot of every column's on-screen width used to freeze
   *  not-yet-sized siblings so only the dragged column changes. */
  setColumnWidth: (key: string, width: number, seed?: Record<string, number>) => void;
  refetch: () => void;
  multiSearchQuery: string;
}

const DEFAULT_SORT: SortToken = { field: 'assetId', direction: 'desc' };

const SESSION_PAGE_NAME = 'PartList';

// Build the initial column defs, layering any browser-session layout
// (order/visibility/widths) on top of the defaults. A saved report still wins —
// it's applied afterwards via applyReportConfig.
const buildSessionColumns = (): ColumnDef[] =>
  applySessionColumnState(buildColumnDefs(), readSessionColumnState(SESSION_PAGE_NAME));

// Keep the picker (and the columns the BFF fetches) in sync with the restored
// columns: start from the default picker tree, then re-tick each field to match
// the session-applied columns' visibility.
const buildSessionColSelection = (): ColumnSelectionItem[] => {
  const visibleKeys = new Set(
    buildSessionColumns()
      .filter((c) => c.visible)
      .map((c) => c.key)
  );
  return buildInitialColumnSelection().map((group) => ({
    ...group,
    value: group.value.map((col) => ({ ...col, value: visibleKeys.has(col.field) })),
  }));
};

export const usePartListData = (): PartListDataReturn => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Hydrate one-shot from URL. State lives in `useState` after this; the
  // writer effect below mirrors changes back to the URL so a back-button
  // remount (the list unmounts when the user clicks into a part) re-reads
  // these params on fresh mount.
  const initialUrlStateRef = useRef(parsePartListUrl(searchParams));
  const initialUrlState = initialUrlStateRef.current;

  const [page, setPage] = useState(initialUrlState.page ?? 0);
  const [pageSize, setPageSize] = useState(initialUrlState.pageSize ?? 25);
  const [sortModel, setSortModel] = useState<SortModel>(() =>
    initialUrlState.sort
      ? { field: initialUrlState.sort.field, direction: initialUrlState.sort.direction }
      : { field: 'assetId', direction: 'desc' }
  );
  // Treat the URL as canonical for chip state and multi-paste serial
  // search only — sort/page/pageSize alone shouldn't drop the Serialised
  // pre-select default that fresh loads expect, and a shared URL like
  // `?multiSearchQuery=AB-001,AB-002` (multi-paste) still needs the
  // codec's `serial1 isAnyOf` filter items to win over EMPTY_FILTERS.
  const urlIsCanonical = initialUrlState.hasChipState || initialUrlState.multiSearchQuery !== '';
  const [filterModel, setFilterModel] = useState<{ items: GridFilterItem[] }>(() =>
    urlIsCanonical ? { items: initialUrlState.filterItems } : buildFilterModel(EMPTY_FILTERS)
  );
  // Multi-paste serial input is encoded into filterModel as `serial1 isAnyOf`,
  // not into the substring multiSearchQuery — clear hook state in that case
  // to mirror the behaviour `applyFilters` enforces below.
  const [multiSearchQuery, setMultiSearchQuery] = useState(() =>
    parseMultiSerial(initialUrlState.multiSearchQuery) !== null
      ? ''
      : initialUrlState.multiSearchQuery
  );
  const [columns, setColumns] = useState<ColumnDef[]>(buildSessionColumns);
  const [colSelectionData, setColSelectionData] =
    useState<ColumnSelectionItem[]>(buildSessionColSelection);
  const { resetColumnsToDefault, restoreDefaultColumns, setColumnWidth } = useSessionColumns({
    setColumns,
    setColSelectionData,
    buildColumns: buildSessionColumns,
    buildColSelection: buildSessionColSelection,
    sessionPageName: SESSION_PAGE_NAME,
  });
  const [fetchDurationMs, setFetchDurationMs] = useState<number | null>(null);
  const fetchStartRef = useRef<number | null>(null);
  const prevDataRef = useRef<Array<Record<string, unknown>>>([]);
  const prevTotalRef = useRef<number>(0);

  const conditionsQuery = useGetPartConditions();
  const tagsQuery = useGetPartAllocatedTags();
  const contractsQuery = useGetStructuresByTypeCategoryCodeAndTypeCode(
    StructureTypeCategory.System,
    NodeType.Contract
  );

  // Mount-only kick for enabled:false queries. The query objects get a new
  // identity every render, so listing them would refetch per render — the
  // refetch-loop class this file is guarded against.
  useEffect(() => {
    contractsQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const conditionOptions = useMemo<LabelValue[]>(
    () => buildConditionOptions(conditionsQuery.data),
    [conditionsQuery.data]
  );

  const tagOptions = useMemo<LabelValue[]>(() => {
    const raw = tagsQuery.data;
    if (!raw) return [];
    return raw.map((t: { text: string }) => ({ label: t.text, value: t.text }));
  }, [tagsQuery.data]);

  const contractOptions = useMemo<LabelValue[]>(
    () => buildContractOptions(contractsQuery.data),
    [contractsQuery.data]
  );

  // useGetAllParts is `enabled: false` — we drive it manually as its inputs change.
  const query = useGetAllParts(
    {
      filterModel,
      page,
      pageSize,
      sortModel: [{ field: sortModel.field, sort: sortModel.direction } as GridSortItem],
    },
    multiSearchQuery || '',
    colSelectionData
  );

  // All manual refetch paths share one loop guard — see useRefetchLoopGuard.
  // The fetch timer starts inside the guarded callback so a call the guard
  // suppresses cannot record a stale start time.
  const guardedRefetch = useRefetchLoopGuard(
    useCallback(() => {
      fetchStartRef.current = performance.now();
      query.refetch();
    }, [query]),
    'Parts list (/partsList)'
  );

  const { schedule: scheduleColumnRefetch, cancel: cancelColumnRefetch } =
    useColumnRefetchScheduler(guardedRefetch);

  useEffect(() => {
    cancelColumnRefetch();
    guardedRefetch();
  }, [
    filterModel,
    sortModel,
    page,
    pageSize,
    multiSearchQuery,
    cancelColumnRefetch,
    guardedRefetch,
  ]);

  // Mirror filter/sort/pagination state to the URL so a back-button remount
  // can re-hydrate from URL params. Replace mode keeps every keystroke out
  // of browser history — only navigation between pages records.
  useEffect(() => {
    const sortToken: SortToken = { field: sortModel.field, direction: sortModel.direction };
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        writePartListUrl(
          next,
          { filterModel, multiSearchQuery, sort: sortToken, page, pageSize },
          DEFAULT_SORT
        );
        return next;
      },
      { replace: true }
    );
  }, [filterModel, multiSearchQuery, sortModel, page, pageSize, setSearchParams]);

  useEffect(() => {
    if (!query.isFetching && fetchStartRef.current !== null) {
      setFetchDurationMs(performance.now() - fetchStartRef.current);
      fetchStartRef.current = null;
    }
  }, [query.isFetching]);

  const applyFilters = useCallback((filters: FilterState) => {
    // Multi-paste input is encoded into the filterModel as `serial1 isAnyOf …`
    // by buildFilterModel. Clear `multiSearchQuery` in that case so the request
    // doesn't also send the substring `serial_search` parameter — the BFF would
    // then treat the comma-joined string as a literal substring and zero-out
    // the result set.
    const isMultiPaste = parseMultiSerial(filters.multiSearch) !== null;
    setMultiSearchQuery(isMultiPaste ? '' : filters.multiSearch || '');
    setFilterModel(buildFilterModel(filters));
    setPage(0);
  }, []);

  const updateColumnSelection = useCallback(
    (data: ColumnSelectionItem[]) => {
      const prevVisible = getVisibleFields(colSelectionData);
      const nextVisible = getVisibleFields(data);
      const hasAddedField = Array.from(nextVisible).some((f) => !prevVisible.has(f));

      setColSelectionData(data);
      setColumns((prev) => prev.map((c) => ({ ...c, visible: nextVisible.has(c.key) })));

      if (hasAddedField) scheduleColumnRefetch();
    },
    [colSelectionData, scheduleColumnRefetch]
  );

  const reorderColumns = useCallback((oldIndex: number, newIndex: number) => {
    setColumns((prev) => reorderByVisibleIndex(prev, oldIndex, newIndex));
  }, []);

  const applyReportConfig = useCallback(
    (
      reportFilterModel: { items: GridFilterItem[] },
      colSelection?: ColumnSelectionItem[],
      columnOrder?: Array<{ field: string }>
    ) => {
      setFilterModel({
        ...reportFilterModel,
        items: sanitizeSavedReportFilterItems(reportFilterModel.items),
      });
      setMultiSearchQuery('');
      setPage(0);
      if (colSelection) updateColumnSelection(colSelection);
      // Reports don't store column widths — drop any user-dragged widths so the
      // report renders in its own (unfrozen) layout, then apply its saved order.
      setColumns((prev) =>
        dropColumnWidths(columnOrder ? applyColumnOrder(prev, columnOrder) : prev)
      );
    },
    [updateColumnSelection, setColumns]
  );

  useEffect(() => {
    const raw = query.data;
    if (raw?.data) {
      prevDataRef.current = raw.data as unknown as Array<Record<string, unknown>>;
      prevTotalRef.current = raw.total ?? 0;
    }
  }, [query.data]);

  const currentData =
    (query.data?.data as unknown as Array<Record<string, unknown>> | undefined) ??
    prevDataRef.current;
  const currentTotal = query.data?.total ?? prevTotalRef.current;

  return {
    data: currentData,
    isLoading: query.isLoading && prevDataRef.current.length === 0,
    isFetching: query.isFetching,
    page,
    pageSize,
    total: currentTotal,
    fetchDurationMs,
    sortModel,
    columns,
    columnSelectionData: colSelectionData,
    filterModel,
    conditionOptions,
    tagOptions,
    contractOptions,
    setPage,
    setPageSize,
    setSortModel,
    applyFilters,
    applyReportConfig,
    updateColumnSelection,
    reorderColumns,
    resetColumnsToDefault,
    restoreDefaultColumns,
    setColumnWidth,
    refetch: guardedRefetch,
    multiSearchQuery,
  };
};
